import React, { useEffect, useRef } from "react";

const toc = [
  ["Introduction", "intro"],
  ["Photons, Energy & Power", "units"],
  ["Spectral Quantities", "spectral"],
  ["Light Directions", "directions"],
  ["Solid Angles", "solid-angle"],
  ["Differential Solid Angle", "differential-sa"],
  ["Hemisphere for Surfaces", "hemisphere"],
  ["Radiant Intensity", "intensity"],
  ["Lambert's Cosine Law", "lamberts"],
  ["Radiance", "radiance"],
  ["Irradiance", "irradiance"],
  ["Reflectance", "reflectance"],
  ["BRDF", "brdf"],
  ["BRDF Properties", "brdf-props"],
  ["Material Types", "material-types"],
  ["Radiosity", "radiosity"],
];

const F = ({ children }) => (
  <div className="formula-block">
    {(Array.isArray(children) ? children : [children]).map((line, i) => (
      <div key={i} className="formula-row">{line}</div>
    ))}
  </div>
);

const Note = ({ label, children }) => (
  <div className="note-box">
    {label && <div className="note-label">{label}</div>}
    {children}
  </div>
);

const Warn = ({ label, children }) => (
  <div className="note-box" style={{ background: "rgba(248,113,113,0.07)", border: "1px solid rgba(248,113,113,0.3)" }}>
    {label && <div className="note-label" style={{ color: "#f87171" }}>{label}</div>}
    {children}
  </div>
);

const Radiometry = ({ renderToc }) => {
  const ref = useRef(null);

  useEffect(() => {
    const tryRender = () => {
      if (window.renderMathInElement && ref.current) {
        window.renderMathInElement(ref.current, {
          delimiters: [
            { left: "$$", right: "$$", display: true },
            { left: "$", right: "$", display: false },
          ],
          throwOnError: false,
        });
      } else {
        setTimeout(tryRender, 100);
      }
    };
    tryRender();
  }, []);

  useEffect(() => {
    if (renderToc) renderToc(toc);
  }, [renderToc]);

  return (
    <div ref={ref}>
      <h1>Radiometry</h1>
      <p className="subtitle">
        Measurement of Light for Computer Graphics &mdash; by Jeff Wilson, Ph.D.
      </p>

      {/* ── INTRO ── */}
      <h2 id="intro">Introduction</h2>
      <p>
        <strong>Radiometry</strong> is the science of measuring light as it
        passes through space, quantifying it in terms of energy and power. In
        computer graphics, radiometric principles underpin physically-based
        rendering: every formula that maps light to a pixel value is ultimately
        derived from radiometry.
      </p>
      <p>
        The central quantity we are building toward is <strong>radiance</strong>,
        which is the quantity a ray tracer actually measures along each ray.
      </p>

      {/* ── UNITS ── */}
      <h2 id="units">Photons, Energy &amp; Power</h2>
      <h3>The photon</h3>
      <p>
        At the quantum level, light consists of <strong>photons</strong>, each
        characterised by a position, a direction of propagation, and a
        wavelength {"$\\lambda$"} (in nanometres). The energy of a single
        photon is given by Planck's equation:
      </p>
      <F>{"$$q = \\frac{hc}{\\lambda}$$"}</F>
      <p>
        where {"$h = 6.626 \\times 10^{-34}\\,\\text{J·s}$"} is Planck's
        constant and {"$c = 2.998 \\times 10^8\\,\\text{m/s}$"} is the speed
        of light. Energy is inversely proportional to wavelength — shorter
        (bluer) photons carry more energy.
      </p>

      <h3>Radiant energy {"$Q$"} and flux {"$\\Phi$"}</h3>
      <p>
        <strong>Radiant energy</strong> {"$Q$"} (joules) is the total energy
        carried by a collection of photons. <strong>Radiant flux</strong>{" "}
        (power) {"$\\Phi$"} is the rate of energy flow:
      </p>
      <F>{"$$\\Phi = \\frac{dQ}{dt} \\qquad \\text{(watts = J/s)}$$"}</F>
      <p>
        A 100 W bulb emits 100 J/s, but not all of that is visible light —
        some is dissipated as heat. Radiant flux counts all electromagnetic
        energy regardless of visibility.
      </p>

      {/* ── SPECTRAL ── */}
      <h2 id="spectral">Spectral Quantities</h2>
      <p>
        A single number for energy or power is insufficient for colour
        rendering. <strong>Spectral quantities</strong> express energy or power
        per unit wavelength interval:
      </p>
      <F>
        {"$$Q_\\lambda = \\frac{dQ}{d\\lambda} \\qquad \\text{(spectral energy, J/nm)}$$"}
        {"$$\\Phi_\\lambda = \\frac{d\\Phi}{d\\lambda} \\qquad \\text{(spectral power, W/nm)}$$"}
      </F>
      <p>
        <strong>Example.</strong> A source emitting 100 W uniformly across
        400–800 nm has spectral power:
      </p>
      <F>{"$$\\Phi_\\lambda = \\frac{100\\,\\text{W}}{800\\,\\text{nm} - 400\\,\\text{nm}} = 0.25\\,\\text{W/nm}$$"}</F>
      <Note label="Continuous assumption">
        Although light is quantised into photons, we assume a continuous
        spectral distribution. Quantum effects become significant only for
        extremely narrow wavelength intervals.
      </Note>

      {/* ── DIRECTIONS ── */}
      <h2 id="directions">Light Directions &amp; Polar Coordinates</h2>
      <p>
        From any point, light can travel in any direction. To describe all
        possible directions we use <strong>spherical polar coordinates</strong>:
      </p>
      <ul>
        <li>
          <strong>Polar angle</strong> {"$\\theta \\in [0, \\pi]$"} — from the
          zenith axis.
        </li>
        <li>
          <strong>Azimuthal angle</strong> {"$\\phi \\in [0, 2\\pi)$"} — around
          the horizontal plane.
        </li>
      </ul>
      <p>
        A direction is a point on the unit sphere, parameterised by{" "}
        {"$(\\theta, \\phi)$"}. To measure the distribution of light at a
        point, imagine sensors on a sphere of radius {"$r$"} surrounding it.
      </p>

      {/* ── SOLID ANGLE ── */}
      <h2 id="solid-angle">Solid Angles &amp; Steradians</h2>
      <p>
        A <strong>solid angle</strong> {"$\\Omega$"} measures how large a patch
        of directions appears from a point — the 2D analogue of a planar angle.
        It equals the area {"$\\Delta S$"} the patch subtends on a unit sphere:
      </p>
      <F>{"$$\\Delta\\Omega = \\frac{\\Delta S}{r^2} \\qquad \\text{(steradians, sr)}$$"}</F>
      <table>
        <thead><tr><th>Region</th><th>Solid angle</th></tr></thead>
        <tbody>
          <tr><td>Full sphere</td><td>{"$4\\pi\\,\\text{sr}$"}</td></tr>
          <tr><td>Hemisphere</td><td>{"$2\\pi\\,\\text{sr}$"}</td></tr>
          <tr><td>Differential patch {"$d\\Omega$"}</td><td>{"$\\sin\\theta\\,d\\theta\\,d\\phi$"}</td></tr>
        </tbody>
      </table>

      {/* ── DIFFERENTIAL SOLID ANGLE ── */}
      <h2 id="differential-sa">Differential Solid Angle</h2>
      <p>
        To integrate over all directions we need the area of an infinitesimally
        thin patch on the unit sphere:
      </p>
      <F>{"$$d\\Omega = \\sin\\theta\\, d\\theta\\, d\\phi$$"}</F>
      <p>
        The {"$\\sin\\theta$"} factor accounts for the shrinking of horizontal
        circles as {"$\\theta$"} approaches the poles — analogous to how lines
        of longitude converge at the Earth's poles.
      </p>
      <p>Integrating over the full sphere confirms the {"$4\\pi$"} result:</p>
      <F>{"$$\\int_0^{2\\pi}\\!\\int_0^{\\pi} \\sin\\theta\\, d\\theta\\, d\\phi = 4\\pi$$"}</F>

      {/* ── HEMISPHERE ── */}
      <h2 id="hemisphere">Hemisphere for Surfaces</h2>
      <p>
        For an opaque surface, light can only arrive from the half-space above
        it. The integration domain is restricted to the upper hemisphere{" "}
        {"$\\mathcal{H}$"}:
      </p>
      <F>
        {"$$\\theta \\in \\left[0,\\,\\frac{\\pi}{2}\\right], \\quad \\phi \\in [0,\\, 2\\pi)$$"}
        {"$$\\int_{\\mathcal{H}} d\\Omega = 2\\pi\\,\\text{sr}$$"}
      </F>
      <p>
        The hemisphere is the default integration domain for all surface
        shading calculations.
      </p>

      {/* ── RADIANT INTENSITY ── */}
      <h2 id="intensity">Radiant Intensity</h2>
      <p>
        <strong>Radiant intensity</strong> {"$I$"} describes how much power a
        light source emits per unit solid angle in a given direction:
      </p>
      <F>{"$$I = \\frac{d\\Phi}{d\\Omega} \\qquad \\text{(W/sr)}$$"}</F>
      <p>
        For an <strong>isotropic</strong> point source, total flux {"$\\Phi$"}{" "}
        is distributed evenly over {"$4\\pi$"} sr:
      </p>
      <F>{"$$I = \\frac{\\Phi}{4\\pi}$$"}</F>
      <p>
        A 100 W isotropic bulb: {"$I = 100/(4\\pi) \\approx 7.96\\,\\text{W/sr}$"}.
      </p>

      {/* ── LAMBERT'S ── */}
      <h2 id="lamberts">Lambert's Cosine Law &amp; Projected Area</h2>
      <p>
        When a surface patch is tilted by angle {"$\\theta$"} relative to the
        light/measurement direction, its apparent area decreases:
      </p>
      <F>{"$$\\Delta A_{\\text{proj}} = \\Delta A \\cos\\theta$$"}</F>
      <p>
        This is <strong>Lambert's Cosine Law</strong>. The radiant power
        intercepted is proportional to the projected area:
      </p>
      <F>{"$$\\Delta\\Phi_{\\text{proj}} = \\Delta\\Phi \\cos\\theta$$"}</F>
      <p>
        Using the <em>projected</em> area rather than the true area in
        radiometric formulas makes measurements invariant to surface tilt — a
        sensor reading does not change just because the sensor is rotated.
      </p>

      {/* ── RADIANCE ── */}
      <h2 id="radiance">Radiance</h2>
      <p>
        <strong>Radiance</strong> {"$L$"} is the fundamental quantity of
        radiometry for graphics. It measures radiant flux per unit solid angle,
        per unit <em>projected</em> area:
      </p>
      <F>{"$$L = \\frac{d^2\\Phi}{dA\\cos\\theta\\; d\\Omega} \\qquad \\text{(W·m}^{-2}\\text{·sr}^{-1}\\text{)}$$"}</F>
      <ul>
        <li>
          Dividing by {"$d\\Omega$"} makes radiance <em>directional</em> —
          it describes light in one specific direction.
        </li>
        <li>
          Dividing by the <em>projected</em> area {"$dA\\cos\\theta$"}{" "}
          compensates for Lambert's law, making the measurement invariant to
          surface tilt.
        </li>
      </ul>
      <Note label="Invariance along a ray">
        Radiance is <strong>constant along a ray in free space</strong>. This
        is the key property that makes it the right quantity for ray tracing:
        the radiance leaving a surface in some direction equals the radiance
        arriving at the next surface. No attenuation calculation is needed
        between surfaces in free space.
      </Note>

      {/* ── IRRADIANCE ── */}
      <h2 id="irradiance">Irradiance</h2>
      <p>
        <strong>Irradiance</strong> {"$H$"} is the total radiant flux incident
        on a surface per unit area, arriving from all directions in the
        hemisphere:
      </p>
      <F>{"$$H = \\int_{\\mathcal{H}} L_{\\text{in}}(\\omega)\\, \\cos\\theta\\; d\\omega \\qquad \\text{(W/m}^2\\text{)}$$"}</F>
      <p>
        Irradiance collapses directional information and gives only the total
        power per area. Radiance is the derivative of irradiance with respect
        to solid angle — you can always integrate radiance to get irradiance,
        but not the reverse.
      </p>
      <Warn label="Terminology warning">
        There is no universally agreed symbol or name for "total outgoing
        radiant flux per unit area." Different texts use {"$M$"}, {"$R$"},{" "}
        {"$B$"} (radiosity), or "exitance." Check each source's conventions.
      </Warn>

      {/* ── REFLECTANCE ── */}
      <h2 id="reflectance">Reflectance</h2>
      <p>
        <strong>Reflectance</strong> {"$\\rho$"} is the ratio of outgoing to
        incoming radiance:
      </p>
      <F>{"$$\\rho = \\frac{L_{\\text{out}}}{L_{\\text{in}}}, \\qquad 0 \\leq \\rho \\leq 1$$"}</F>
      <ul>
        <li>
          <strong>Linearity.</strong> Scaling incoming radiance by {"$k$"}{" "}
          scales outgoing radiance by the same factor (if {"$\\rho$"} is
          constant).
        </li>
        <li>
          <strong>Energy conservation.</strong> {"$\\rho \\leq 1$"} — no
          surface can reflect more energy than it receives.
        </li>
      </ul>

      {/* ── BRDF ── */}
      <h2 id="brdf">Bidirectional Reflectance Distribution Function (BRDF)</h2>
      <p>
        Real materials reflect differently depending on both the incoming and
        outgoing directions. The <strong>BRDF</strong>{" "}
        {"$\\rho(\\mathbf{k}_i, \\mathbf{k}_o)$"} is a function of these two
        directions that fully describes surface scattering:
      </p>
      <F>
        {"$$\\rho(\\mathbf{k}_i, \\mathbf{k}_o) = \\frac{L_{\\text{out}}(\\mathbf{k}_o)}{H(\\mathbf{k}_i)} \\qquad \\text{(sr}^{-1}\\text{)}$$"}
      </F>
      <p>
        Each direction is expressed as {"$(\\theta, \\phi)$"}, making the BRDF
        a <strong>4D function</strong>. This high dimensionality motivates the
        special-case material models below.
      </p>
      <h3>The rendering integral</h3>
      <p>
        Total outgoing radiance in direction {"$\\mathbf{k}_o$"} is found by
        integrating the BRDF against all incoming radiance:
      </p>
      <F>
        {"$$L_{\\text{out}}(\\mathbf{k}_o) = \\int_{\\mathcal{H}} \\rho(\\mathbf{k}_i, \\mathbf{k}_o)\\; L_{\\text{in}}(\\mathbf{k}_i)\\; \\cos\\theta_i\\; d\\Omega_i$$"}
      </F>
      <p>
        <strong>Discrete evaluation</strong> (direct illumination): for point
        lights the integral collapses to a sum — this is what Phong shading
        does. <strong>Continuous evaluation</strong> (global illumination):
        integrate over all directions, capturing indirect light; requires
        Monte Carlo sampling.
      </p>

      {/* ── BRDF PROPERTIES ── */}
      <h2 id="brdf-props">BRDF Properties</h2>
      <h3>Energy conservation</h3>
      <p>
        The total reflected fraction cannot exceed 1 for any incoming direction:
      </p>
      <F>{"$$\\int_{\\mathcal{H}} \\rho(\\mathbf{k}_i, \\mathbf{k}_o)\\; \\cos\\theta_o\\; d\\Omega_o \\;\\leq\\; 1$$"}</F>

      <h3>Helmholtz reciprocity</h3>
      <p>
        Swapping incoming and outgoing directions leaves the BRDF unchanged:
      </p>
      <F>{"$$\\rho(\\mathbf{k}_i, \\mathbf{k}_o) = \\rho(\\mathbf{k}_o, \\mathbf{k}_i)$$"}</F>
      <p>
        This reflects reversible light paths and is essential for bidirectional
        rendering algorithms.
      </p>

      {/* ── MATERIAL TYPES ── */}
      <h2 id="material-types">Material Types &amp; BRDF Models</h2>
      <h3>Diffuse (Lambertian)</h3>
      <p>
        Reflects light uniformly across the hemisphere independent of viewing
        angle. The BRDF is a constant:
      </p>
      <F>{"$$\\rho(\\mathbf{k}_i, \\mathbf{k}_o) = \\frac{\\rho_d}{\\pi}$$"}</F>
      <p>
        The {"$\\pi$"} normalises the hemisphere integral to {"$\\rho_d$"}.
        Despite a constant BRDF, observed radiance still varies with{" "}
        {"$\\theta_i$"} via the {"$\\cos\\theta_i$"} term in the rendering
        integral.
      </p>

      <h3>Glossy / specular</h3>
      <p>
        Reflects preferentially toward directions near the specular reflection
        angle, producing highlights. Depends on the relative azimuth and polar
        angles of incoming and outgoing directions. In Phong shading:
      </p>
      <F>{"$$\\rho \\propto \\max(0,\\, \\mathbf{r}\\cdot\\mathbf{k}_o)^s$$"}</F>
      <p>
        where {"$\\mathbf{r}$"} is the mirror-reflected direction of{" "}
        {"$\\mathbf{k}_i$"} and {"$s$"} is the shininess exponent.
      </p>

      <h3>Mirror (perfect specular)</h3>
      <p>
        Reflects all light into exactly one direction. The BRDF is a Dirac
        delta:
      </p>
      <F>{"$$\\rho(\\mathbf{k}_i, \\mathbf{k}_o) = \\delta(\\mathbf{k}_o - \\mathbf{r})$$"}</F>
      <p>
        Nonzero only when {"$\\mathbf{k}_o$"} exactly equals the reflection
        direction, collapsing the hemisphere integral to a single evaluation.
      </p>

      <h3>Isotropic vs. anisotropic</h3>
      <p>
        An <strong>isotropic</strong> material looks the same regardless of
        rotation about its normal — the BRDF depends only on the relative
        azimuth {"$(\\phi_o - \\phi_i)$"} (3 degrees of freedom). An{" "}
        <strong>anisotropic</strong> material has directional surface structure
        (grooves, fibers) and requires all four arguments (4 degrees of
        freedom).
      </p>
      <F>
        {"$\\text{Isotropic: } \\rho(\\theta_i,\\, \\phi_o - \\phi_i,\\, \\theta_o)$"}
        {"$\\text{Anisotropic: } \\rho(\\theta_i,\\, \\phi_i,\\, \\theta_o,\\, \\phi_o)$"}
      </F>

      {/* ── RADIOSITY ── */}
      <h2 id="radiosity">Radiosity</h2>
      <p>
        <strong>Radiosity</strong> is a global illumination method that
        computes the steady-state transfer of diffuse light energy between all
        surface patches in a scene. It assumes all surfaces are Lambertian,
        enabling a view-independent precomputation.
      </p>
      <p>
        The scene is discretised into {"$N$"} patches. The radiosity{" "}
        {"$B_i$"} (outgoing power per unit area) satisfies a linear system:
      </p>
      <F>{"$$B_i = E_i + \\rho_i \\sum_{j=1}^{N} B_j\\, F_{ij}$$"}</F>
      <p>
        where {"$E_i$"} is emitted power per unit area, {"$\\rho_i$"} is
        diffuse reflectance, and {"$F_{ij}$"} is the <strong>form factor</strong>{" "}
        — the fraction of flux leaving patch {"$j$"} that arrives at patch{" "}
        {"$i$"} (determined by geometry alone).
      </p>
      <p>
        Once the form factors are precomputed, solving the system gives full
        indirect illumination for every patch. Radiosity was used in early ray
        tracers (POV-Ray) and game engines (Quake's lightmaps) to bake indirect
        lighting into static scenes. Its main limitation: the Lambertian
        assumption excludes glossy inter-reflections.
      </p>

      {/* ── SUMMARY ── */}
      <h2 id="summary">Summary</h2>
      <table>
        <thead>
          <tr><th>Quantity</th><th>Symbol</th><th>Definition</th><th>Units</th></tr>
        </thead>
        <tbody>
          <tr><td>Radiant energy</td><td>{"$Q$"}</td><td>Total EM energy</td><td>J</td></tr>
          <tr><td>Radiant flux</td><td>{"$\\Phi$"}</td><td>{"$dQ/dt$"}</td><td>W</td></tr>
          <tr><td>Radiant intensity</td><td>{"$I$"}</td><td>{"$d\\Phi/d\\Omega$"}</td><td>W/sr</td></tr>
          <tr><td>Irradiance</td><td>{"$H$"}</td><td>{"$\\int L\\cos\\theta\\,d\\Omega$"}</td><td>W/m²</td></tr>
          <tr><td>Radiance</td><td>{"$L$"}</td><td>{"$d^2\\Phi/(dA\\cos\\theta\\,d\\Omega)$"}</td><td>W·m⁻²·sr⁻¹</td></tr>
          <tr><td>BRDF</td><td>{"$\\rho(\\mathbf{k}_i,\\mathbf{k}_o)$"}</td><td>{"$L_{\\text{out}}(\\mathbf{k}_o)/H(\\mathbf{k}_i)$"}</td><td>sr⁻¹</td></tr>
        </tbody>
      </table>
      <p>
        Radiance is the central quantity for ray tracing because it is invariant
        along a ray, combines all relevant factors (flux, area, angle,
        direction), and directly corresponds to what a camera sensor measures.
        The BRDF then describes how each surface transforms incoming radiance
        into outgoing radiance as a function of direction.
      </p>
    </div>
  );
};

export default Radiometry;
